import React, { RefObject, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Decimal from 'decimal.js';
import { ExportFileType, ExportVersionFile } from 'common/types/export-version-file';
import { PlayerProps, PlayerRef } from '@/types/playControl';
import { getVideoBase64 } from '@/utils';

import { useTaskEditorContext } from '@/context/TaskEditorContext';
import { handleVideoProgressEvent, useVideoLoading } from './util';
import cx from './index.module.css';

const MediaPlayer = forwardRef<PlayerRef, PlayerProps>(
  (
    {
      onLoad,
      onFrameChange,
      url,
      fps = 24,
      totalFrames = 0,
      crossOrigin = 'use-credentials',
      onError,
      onPause,
      type,
      playList,
      isPlayListMode,
      currentPlayIndex,
      onCurrentPlayIndexChange,
    },
    ref,
  ) => {
    const { setCurrentFile } = useTaskEditorContext();
    const mediaRef = useRef<HTMLVideoElement | HTMLImageElement | null>(null);

    // video元素通过时间控制播放进度
    const [loop, setLoop] = useState(true);
    const loopRef = useRef<boolean>(loop);
    // rAF 的 id 只用于取消，不需要参与渲染。放在 state 里会导致每帧一次 setState，
    // 进而让依赖它的 effect 每帧销毁重建
    const requestIdRef = useRef<number | null>(null);
    const [currentUrl, setCurrentUrl] = useState(url);
    const currentPlayIndexRef = useRef<number | undefined>(currentPlayIndex);

    const [isPaused, setIsPaused] = useState(true);
    const pausedRef = useRef<boolean>(isPaused);

    const playListVideoRef = useRef<HTMLVideoElement | null>(null);

    const videoRef = useMemo(() => {
      return isPlayListMode ? playListVideoRef : mediaRef;
    }, [isPlayListMode]);

    // loading hooks
    useVideoLoading(videoRef, type);

    // stuck hooks
    // useVideoAvoidStuck(mediaRef, totalFrames);

    // const [videoObjects, setVideoObjects] = useState<
    //   {
    //     element: HTMLVideoElement;
    //     item: ExportVersionFile;
    //   }[]
    // >([]);
    const videoListRef = useRef<
      {
        element: HTMLVideoElement;
        item: ExportVersionFile;
      }[]
    >([]);

    useEffect(() => {
      if (!playList?.length) return;
      videoListRef.current =
        playList?.map(item => {
          const element = document.createElement('video');
          element.src = item.exportFileUrl[0];
          element.currentTime = 0.01;
          element.style.width = '100%';
          element.style.height = '100%';
          element.preload = 'auto';
          element.loop = false;
          element.crossOrigin = crossOrigin;
          return {
            element,
            item,
          };
        }) || [];
    }, [playList, crossOrigin]);

    const changeCurrentPlayIndex = useCallback(
      (currentPlayIndex, currentTime?) => {
        const videoElement = document.getElementById('video-wrapper');
        if (!playList?.length || currentPlayIndex === undefined || !videoListRef.current?.length || !videoElement) {
          return;
        }

        if (playListVideoRef.current) {
          playListVideoRef.current.pause();
          playListVideoRef.current.currentTime = 0;
        }

        const s = videoListRef.current[currentPlayIndex];
        const item = s.item;
        changeUrl(item.exportFileUrl[0]);

        setCurrentFile(item);

        videoElement.innerHTML = '';
        if (!isPlayListMode) return;

        videoElement.appendChild(s.element);
        s.element.playbackRate = speedRef.current;
        currentPlayIndexRef.current = currentPlayIndex;
        playListVideoRef.current = s.element;

        s.element.currentTime = currentTime ?? s.element.currentTime;
        if (pausedRef.current) {
          playListVideoRef.current?.pause();
        } else {
          playListVideoRef.current?.play();
        }
        updateFrameBar();
      },
      [isPlayListMode, playList],
    );

    useEffect(() => {
      if (!isPlayListMode) {
        const videoElement = document.getElementById('video-wrapper');
        if (videoElement) {
          videoElement.innerHTML = '';
        }

        // changeCurrentPlayIndex(currentPlayIndex);
      }
    }, [isPlayListMode]);
    const goToStartFrame = (frameIndex: number) => {
      if (!playList?.length) return;
      const item = playList[frameIndex];
      changeUrl(item.exportFileUrl[0]);
    };

    const changeUrl = newUrl => {
      setCurrentUrl(newUrl);
    };
    // useEffect(() => {
    //   if (currentUrl !== url) {
    //     changeUrl(url);
    //   }
    // }, [url]);

    const speedRef = useRef(1);
    const handleSpeedChange = newSpeed => {
      speedRef.current = newSpeed;
      console.log('speedRef.current', speedRef.current);
      if (playListVideoRef.current) {
        Object.assign(playListVideoRef.current, { playbackRate: newSpeed });
      }
      if (mediaRef.current) {
        Object.assign(mediaRef.current, { playbackRate: newSpeed });
      }
    };

    const handleGoToFrame = useCallback(
      (frameIndex: number, isMouseDown?: boolean) => {
        if (!videoRef.current) return;
        if (videoRef.current instanceof HTMLVideoElement) {
          let t = 0;
          let currentTime = 0;
          if (isPlayListMode && playList?.length) {
            for (let i = 0; i < playList.length; i++) {
              const item = playList[i];
              const totalFrames = item.rules.find(rule => rule.ruleType === 'TotalFrames')?.checkResult as number;
              const fps = item.rules.find(rule => rule.ruleType === 'FrameRate')?.checkResult as number;

              if (t + totalFrames > frameIndex) {
                const duration = totalFrames / fps;
                currentTime = (frameIndex - t) / fps;
                if (currentUrl !== item.exportFileUrl[0]) {
                  changeCurrentPlayIndex(i);
                  currentPlayIndexRef.current = i;
                  changeUrl(item.exportFileUrl[0]);
                  onCurrentPlayIndexChange?.(i);
                }

                videoRef.current.currentTime = currentTime;

                break;
              }
              t += totalFrames;
            }
          } else {
            const duration = videoRef.current.duration;
            //用decimal.js解决精度问题
            currentTime = new Decimal(duration).mul(frameIndex).div(totalFrames).toNumber();
            const isLegal = !isNaN(currentTime) && currentTime > 0;
            if (isLegal) {
              videoRef.current.currentTime = currentTime;
            }
          }
          console.log(isPlayListMode, 'currentTime', currentTime, videoRef.current);
          updateFrameBar(currentTime);
        }
      },
      [totalFrames, videoRef.current, playList, isPlayListMode, currentUrl, changeCurrentPlayIndex],
    );

    const updateFrameBar = useCallback(
      (time?: number) => {
        if (videoRef.current instanceof HTMLVideoElement) {
          if (!videoRef.current) return;

          const currentTime = time ?? videoRef.current.currentTime;
          const duration = videoRef.current.duration;
          let currentTotalFrames = totalFrames;
          let currentFrame = 0;
          let offset = 0;
          if (isPlayListMode && playList?.length) {
            for (let i = 0; i < playList.length; i++) {
              const item = playList[i];
              if (currentPlayIndexRef.current === i) {
                const fps = item.rules.find(rule => rule.ruleType === 'FrameRate')?.checkResult as number;
                currentFrame = new Decimal(fps).mul(currentTime).add(offset).toNumber();
                break;
              }

              const totalFrames = item.rules.find(rule => rule.ruleType === 'TotalFrames')?.checkResult as number;
              offset += totalFrames;
            }
          } else {
            currentFrame = new Decimal(currentTotalFrames).mul(currentTime).div(duration).toNumber();
          }
          onFrameChange(isNaN(currentFrame) ? 0 : currentFrame, offset); // 更新当前帧

          if (isPlayListMode && playList?.length) {
            if (videoRef.current?.ended && currentPlayIndexRef.current !== undefined) {
              if (currentPlayIndexRef.current < playList.length - 1) {
                goToStartFrame(currentPlayIndexRef.current + 1);
                onCurrentPlayIndexChange?.(currentPlayIndexRef.current + 1);
                changeCurrentPlayIndex(currentPlayIndexRef.current + 1);
              } else if (loopRef.current) {
                goToStartFrame(0);
                onCurrentPlayIndexChange?.(0);
                changeCurrentPlayIndex(0);
              } else {
                goToStartFrame(0);
                onCurrentPlayIndexChange?.(0);
                changeCurrentPlayIndex(0);
                videoRef.current?.pause();
                updatePaused(true);
              }
            }
          } else {
            const isEnd = videoRef.current?.ended && !loopRef.current;
            if (isEnd) {
              videoRef.current?.pause();
              updatePaused(true);
            }
          }

          handleVideoProgressEvent(videoRef);
        }
      },
      [totalFrames, isPaused, loop, currentPlayIndex, isPlayListMode, playList, changeCurrentPlayIndex],
    );

    const handleFrame = useCallback(() => {
      updateFrameBar();
      requestIdRef.current = requestAnimationFrame(handleFrame);
    }, [updateFrameBar]);

    const updatePaused = useCallback((paused: boolean) => {
      setIsPaused(paused);
      pausedRef.current = paused;
      onPause?.(paused);
    }, []);

    const updateLoop = useCallback((loop: boolean) => {
      setLoop(loop);
      loopRef.current = loop;
    }, []);
    const play = useCallback(
      (loop?: boolean, name?: string) => {
        //判断mediaRef.current是video还是image
        console.log('play', videoRef.current);
        if (!videoRef.current) return;
        updatePaused(false);
        handleFrame();
        if (videoRef.current instanceof HTMLVideoElement) {
          videoRef.current?.play();
          updateLoop(!!loop);
        }
      },
      [handleFrame, updateLoop],
    );

    const handleLoadedMetadata = useCallback(() => {
      if (!videoRef.current) {
        return;
      }
      onLoad([
        {
          name: '',
          frames: totalFrames,
          fps,
        },
      ]);
      // requestIdRef.current = requestAnimationFrame(handleFrame); // 多次启动
    }, [onLoad, handleFrame, totalFrames, type, fps]);

    useEffect(() => {
      if (type === ExportFileType.Video) {
        videoRef.current?.addEventListener('loadedmetadata', handleLoadedMetadata);
      }

      return () => {
        if (type === ExportFileType.Video) {
          videoRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata);
          if (requestIdRef.current) {
            cancelAnimationFrame(requestIdRef.current);
          }
        }
      };
    }, [type, url]); // 为什么有fps依赖？

    // 增强 ref 的能力，添加方法控制播放状态
    // eslint-disable-next-line react-func/max-lines-per-function
    React.useImperativeHandle(ref, () => ({
      getElement: () => videoRef.current,
      setSpeedRatio: (ratio?: number) => {
        handleSpeedChange(ratio || 1);
      },
      goToFrame: (frameIndex: number, name?: string) => {
        if (!videoRef.current) return;
        console.log('goToFrame', videoRef.current);
        handleGoToFrame(frameIndex);
      },
      goToStartFrame: goToStartFrame,
      changeCurrentPlayIndex: changeCurrentPlayIndex,
      play: play,
      pause: () => {
        if (!videoRef.current) return;
        if (videoRef.current instanceof HTMLVideoElement) {
          videoRef.current?.pause();
          if (requestIdRef.current) {
            cancelAnimationFrame(requestIdRef.current);
          }
          updatePaused(true);
        }
      },
      stop: (name?: string) => {
        if (!videoRef.current) return;
        if (videoRef.current instanceof HTMLVideoElement) {
          videoRef.current?.pause();
          if (requestIdRef.current) {
            cancelAnimationFrame(requestIdRef.current);
          }
          // handleGoToFrame(1);
          updatePaused(true);
        }
      },
      setVolume: (volume: number) => {
        if (!videoRef.current) return;
        const validVolume = volume / 100;
        !isNaN(validVolume) && Object.assign(videoRef.current, { volume: validVolume });
      },
      getCurrentTime: () => {
        if (videoRef.current && videoRef.current instanceof HTMLVideoElement) {
          return videoRef.current.currentTime;
        }
        return 0;
      },
      takeSnapshot: async () => {
        if (!videoRef.current) return { image: '', params: null };
        if (type === ExportFileType.Image) return { image: url, params: null };
        const video = videoRef.current as HTMLVideoElement;
        const data = await getVideoBase64(video);
        const { volume, playbackRate, currentTime } = video;
        return { image: data, params: { volume, playbackRate, currentTime } };
      },
      restoreSnapshot: snapshot => {
        if (!videoRef.current) return;
        if (videoRef.current instanceof HTMLVideoElement) {
          const params = snapshot.params;
          Object.assign(videoRef.current, params);
        }
      },
    }));

    return (
      <>
        {type === ExportFileType.Video && (
          <>
            <video
              style={{ display: isPlayListMode ? 'none' : 'block' }}
              ref={mediaRef as RefObject<HTMLVideoElement>}
              loop={!isPlayListMode && loop}
              className={cx['video-player']}
              // onLoadedMetadata={handleLoadedMetadata}
              controls={false}
              src={url}
              preload="auto"
              width="100%"
              height="100%"
              crossOrigin={crossOrigin}
              disablePictureInPicture={true}
              onError={() => {
                onError && onError();
              }}>
              您的浏览器暂不支持video播放，建议使用
              <a href="https://videojs.com/html5-video-support/" target="_blank" rel="noreferrer">
                以下浏览器
              </a>
            </video>
            <div id="video-wrapper" className="w-full h-full"></div>
          </>
        )}

        {type === ExportFileType.Image && (
          <img
            src={url}
            alt=""
            width="100%"
            height="100%"
            ref={mediaRef as RefObject<HTMLImageElement>}
            className={cx['image-player']}
            onLoad={handleLoadedMetadata}
          />
        )}
      </>
    );
  },
);

export default MediaPlayer;
